import { useRef } from "react";
import type { ComponentProps, FunctionComponent, SVGProps } from "react";

import PencilIcon from "@assets/icons/pencil.svg";
import CopyIcon from "@assets/icons/copy.svg";
import TrashIcon from "@assets/icons/trash.svg";

import type { ITooltipComponent } from "../../extensions/Link";
import { Tooltip } from "@ui/Tooltip";
import { Button } from "@ui/Button";

export type ILinkPreviewProps = ComponentProps<ITooltipComponent>;

const LinkPreviewButton = ({
  root,
  onClick,
  label,
  icon,
}: {
  root: HTMLElement;
  onClick: () => void;
  label: string;
  icon: FunctionComponent<SVGProps<SVGSVGElement>>;
}) => {
  return (
    <Tooltip
      className="flex-none p-0"
      tooltipContent={label}
      placement="bottom"
      variant="dark"
      arrow
      offset={8}
      root={root}
    >
      <Button
        type="button"
        buttonType="TRANSPARENT"
        onClick={onClick}
        className="h-7 w-7 flex-none p-0 dark:text-white dark:hover:bg-gray-600"
        isIconOnly
        PrefixIcon={icon}
        PrefixIconClass="text-text-secondary dark:text-invertedPrimary80"
      />
    </Tooltip>
  );
};

export const LinkPreview = ({ href, onCopy, onDelete, onEdit }: ILinkPreviewProps) => {
  const ref = useRef<HTMLDivElement>(null);
  return (
    <div
      ref={ref}
      className="flex items-center gap-2 rounded-lg border border-stroke-primary dark:border-gray-600 bg-background-primary dark:bg-gray-800 px-2 py-1"
    >
      <div className="flex gap-1">
        <img
          className="h-5 w-5 overflow-hidden rounded-md object-cover"
          src={`https://www.google.com/s2/favicons?domain=${href}&sz=40`}
          aria-hidden
        />
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer nofollow"
          className="max-w-[180px] text-sm font-medium text-text-primary dark:text-invertedPrimary80 text-overflow-ellipsis"
        >
          {href}
        </a>
      </div>
      <div className="flex gap-0.5">
        <LinkPreviewButton
          root={ref.current?.parentElement as HTMLElement}
          onClick={onEdit}
          label="Edit link"
          icon={PencilIcon}
        />
        <LinkPreviewButton
          root={ref.current?.parentElement as HTMLElement}
          onClick={onCopy}
          label="Copy link"
          icon={CopyIcon}
        />
        <LinkPreviewButton
          root={ref.current?.parentElement as HTMLElement}
          onClick={onDelete}
          label="Delete link"
          icon={TrashIcon}
        />
      </div>
    </div>
  );
};
